import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { QuoteFormOptions, QuoteListResult } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";

/** Propostas comerciais (RF-VEN-01): lista por número, busca por número ou cliente e filtro de situação. */
export function QuotesListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusId, setStatusId] = useState("");
  const term = q.trim();
  const canCreate = can(perms, "quotes", "create");
  const canEdit = canCreate || can(perms, "quotes", "update");
  // As situações vêm das opções do formulário (sem custo nem % de lista); quem só lê fica sem o filtro.
  const options = useQuery({
    queryKey: ["quotes", companyId, "form-options"],
    enabled: canEdit,
    queryFn: () => api<QuoteFormOptions>("/quotes/form-options"),
  });
  const list = useInfiniteQuery({
    queryKey: ["quotes", companyId, "list", term, statusId],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<QuoteListResult>(
        `/quotes?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(statusId ? { statusId } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const create = () => navigate({ to: "/vendas/propostas/novo" });
  return (
    <>
      <ContentHeader
        title="Propostas comerciais"
        breadcrumb={["Vendas", "Propostas comerciais"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={create}>
              Incluir proposta
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-6">
            <label className="visually-hidden" htmlFor="quotes-q">
              Pesquisar proposta
            </label>
            <input
              id="quotes-q"
              type="search"
              className="form-control"
              placeholder="Pesquisar por número ou cliente"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {options.data && (
            <div className="col-md-3">
              <label className="visually-hidden" htmlFor="quotes-status">
                Situação
              </label>
              <select
                id="quotes-status"
                className="form-select"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">Todas as situações</option>
                {options.data.statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma proposta"
            description="Monte propostas com os preços da lista do cliente, gere o PDF e converta em pedido quando aprovar."
            actions={canCreate ? [{ label: "Incluir proposta", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Propostas comerciais">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Data</th>
                    <th scope="col">Cliente</th>
                    <th scope="col">Vendedor</th>
                    <th scope="col">Validade</th>
                    <th scope="col" className="text-end">
                      Total
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/vendas/propostas/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                      </td>
                      <td>{formatDateBR(r.date)}</td>
                      <td>{r.contactName}</td>
                      <td>{r.sellerName ?? "—"}</td>
                      <td>{r.validUntil ? formatDateBR(r.validUntil) : "—"}</td>
                      <td className="text-end">{formatBRL(r.totalCents)}</td>
                      <td>
                        <StatusBadge tone={r.status.color}>{r.status.label}</StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && (
                <span className="text-muted small" data-testid="quotes-summary">
                  {summary.count} {summary.count === 1 ? "proposta" : "propostas"}
                  {summary.totalCents !== null && ` · total ${formatBRL(summary.totalCents)}`}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
